import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './Soon.css';
import logo from './logo.png';
import '@fortawesome/fontawesome-free/css/all.min.css';
import backgroundImage from './dark.png';

function Soon() {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isChangingLang, setIsChangingLang] = useState(false);

    // Countdown target date - adjust as needed
    const targetDate = new Date('2026-09-01T00:00:00');

    const calculateCountdown = useCallback(() => {
        const now = new Date();
        const difference = targetDate - now;

        if (difference > 0) {
            setCountdown({
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            });
        }
    }, []);

    useEffect(() => {
        calculateCountdown();
        const timer = setInterval(calculateCountdown, 1000);
        return () => clearInterval(timer);
    }, [calculateCountdown]);

    // Email validation
    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        setIsValidEmail(validateEmail(value));
        setError(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(false);
        setIsLoading(true);

        try {
            const response = await fetch('https://formspree.io/f/xvgoogob', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            if (response.ok) {
                setSubmitted(true);
            } else {
                setError(true);
            }
        } catch (error) {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleLanguage = () => {
        setIsChangingLang(true);
        setTimeout(() => {
            const newLang = i18n.language === 'en' ? 'pt' : 'en';
            i18n.changeLanguage(newLang);
            setTimeout(() => {
                setIsChangingLang(false);
            }, 50);
        }, 200);
    };

    return (
        <div className={`soon-container ${isChangingLang ? 'lang-changing' : ''}`} style={{ backgroundImage: `url(${backgroundImage})` }}>
            {/* Animated background particles */}
            <div className="particles">
                {[...Array(15)].map((_, i) => (
                    <div key={i} className="particle" style={{
                        left: `${(i * 7) % 100}%`,
                        animationDelay: `${i * 0.8}s`,
                        animationDuration: `${18 + (i % 5) * 3}s`
                    }} />
                ))}
            </div>

            {/* Language Toggle Button */}
            <div className="language-toggle">
                <button onClick={toggleLanguage} className="lang-button">
                    <i className="fas fa-globe"></i>
                    <span>{i18n.language.toUpperCase()}</span>
                </button>
            </div>

            <div className="soon-header">
                <img src={logo} alt="Company Logo" className="soon-logo" />
                
                <div className="badge-container">
                    <span className="status-badge">
                        <span className="pulse-dot"></span>
                        {t('soon.title')}
                    </span>
                </div>

                <h1 className="soon-title">
                    {t('soon.workingMessage')}
                </h1>
                <p className="soon-text subtle-message">{t('soon.subtleMessage')}</p>

                {/* Countdown Timer */}
                <div className="countdown-container">
                    <div className="countdown-item">
                        <span className="countdown-value">{String(countdown.days).padStart(2, '0')}</span>
                        <span className="countdown-label">{t('soon.days')}</span>
                    </div>
                    <div className="countdown-separator">:</div>
                    <div className="countdown-item">
                        <span className="countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
                        <span className="countdown-label">{t('soon.hours')}</span>
                    </div>
                    <div className="countdown-separator">:</div>
                    <div className="countdown-item">
                        <span className="countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
                        <span className="countdown-label">{t('soon.minutes')}</span>
                    </div>
                    <div className="countdown-separator">:</div>
                    <div className="countdown-item">
                        <span className="countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
                        <span className="countdown-label">{t('soon.seconds')}</span>
                    </div>
                </div>
            </div>

            <div className="soon-content">
                {!submitted && (
                    <>
                        <div className="content-icon">
                            <i className="fas fa-envelope-open-text"></i>
                        </div>
                        <h2 className="subscribe-title">{t('soon.notifyTitle')}</h2>
                        <p className="subscribe-message">{t('soon.subscribeMessage')}</p>
                    </>
                )}

                {submitted ? (
                    <div className="success-container">
                        <div className="success-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <p className="success-message">{t('soon.successMessage')}</p>
                        <p className="success-submessage">{t('soon.successSubmessage')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="soon-form">
                        <div className={`input-wrapper ${email ? (isValidEmail ? 'valid' : 'invalid') : ''}`}>
                            <i className="fas fa-envelope input-icon"></i>
                            <input
                                type="email"
                                name="email"
                                className="soon-input"
                                placeholder={t('soon.emailPlaceholder')}
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isLoading}
                            />
                            {email && (
                                <i className={`fas ${isValidEmail ? 'fa-check validation-icon valid' : 'fa-times validation-icon invalid'}`}></i>
                            )}
                        </div>
                        <button 
                            type="submit" 
                            className={`soon-button ${isLoading ? 'loading' : ''}`}
                            disabled={isLoading || !isValidEmail}
                        >
                            {isLoading ? (
                                <span className="button-loader"></span>
                            ) : (
                                <>
                                    <span>{t('soon.subscribeButton')}</span>
                                    <i className="fas fa-arrow-right"></i>
                                </>
                            )}
                        </button>
                    </form>
                )}

                {error && (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i>
                        <p className="error-message">{t('soon.errorMessage')}</p>
                    </div>
                )}

                {/* Trust indicators */}
                <div className="trust-indicators">
                    <div className="trust-item">
                        <i className="fas fa-shield-alt"></i>
                        <span>{t('soon.privacy')}</span>
                    </div>
                    <div className="trust-item">
                        <i className="fas fa-bell-slash"></i>
                        <span>{t('soon.noSpam')}</span>
                    </div>
                </div>
            </div>

            {/* Footer Section */}
            <div className="footer">
                <div className="social-follow">
                    <p className="social-text">{t('soon.socialText')}</p>
                    <div className='socials-icons-wrapper'>
                        <a
                            href="https://www.instagram.com/tecnasolutions.pt/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-icon-link instagram"
                        >
                            <i className="fab fa-instagram"></i>
                        </a>
                        <a
                            href="https://www.linkedin.com/company/tecna-digital-solutions/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-icon-link linkedin"
                        >
                            <i className="fab fa-linkedin-in"></i>
                        </a>
                    </div>
                </div>
            </div>

            <p className="copyright">© 2026 Tecna Digital Solutions. {t('soon.rights')}</p>
        </div>
    );
}

export default Soon;
